/* eslint-disable sonarjs/cognitive-complexity */
import { addDays, format, formatISO, isSameDay, parseISO, startOfDay } from 'date-fns';
import * as React from 'react';
import { Container, Form, Nav, Row } from 'react-bootstrap';
import { Outlet, useLocation, useMatches, useRevalidator, useSearchParams } from 'react-router-dom';

import { InfoTable } from '@/components/infotable';
import { InputTextArea } from '@/components/input-textarea';
import { ModalDelete } from '@/components/modal-delete';
import { ModalForm } from '@/components/modal-form';
import { ModalWarning } from '@/components/modal-warning';
import { PageHeader } from '@/components/pageheader';
import { SearchBarDate } from '@/components/search-bardate';
import { SearchButtonSessions } from '@/components/search-button-sessions';
import { SelectorAPI } from '@/components/selector-api';
import { SelectorAPIGroup } from '@/components/selector-api-group';
import { SelectorAPIHelper } from '@/components/selector-api-helper';
import { SelectorAPIMultiple } from '@/components/selector-api-multiple';
import { SelectorAPISimple } from '@/components/selector-api-simple';
import { SelectorDate } from '@/components/selector-date';
import { SelectorDateTime } from '@/components/selector-datetime';
import { SelectorList } from '@/components/selector-list';
import { SelectorPad } from '@/components/selector-pad';
import { SelectorPta } from '@/components/selector-pta';
import { Switch } from '@/components/switch';
import { getSessionsById } from '@/helper/apicalls';
import { Holiday, Match, Pta, Session } from '@/helper/interfaces';
import { allStatuses, backupDrives, frameRates } from '@/helper/lists';
import { useStore } from '@/store';

interface BulkValues {
  startDate?: string;
  startTime?: string;
  endTime?: string;
  pastMidnight?: boolean;
  companyId?: number;
  contactId?: number;
  projectId?: number;
  prePadHours?: string;
  postPadHours?: string;
  mixerId?: number;
  assistantIds?: number[];
  roomId?: number;
  status?: string;
  notes?: string;
  unsupervised?: boolean;
  noCharge?: boolean;
}

interface BulkMixed {
  startDate: boolean;
  startTime: boolean;
  endTime: boolean;
  pastMidnight: boolean;
  company: boolean;
  contact: boolean;
  project: boolean;
  prePadHours: boolean;
  postPadHours: boolean;
  mixer: boolean;
  assistant: boolean;
  room: boolean;
  status: boolean;
  notes: boolean;
  unsupervised: boolean;
  noCharge: boolean;
}

interface BulkDefaults {
  values: BulkValues;
  mixed: BulkMixed;
}

function getAssistantIds(session: Session) {
  if (Array.isArray(session.assistantIds)) return session.assistantIds;
  if (Array.isArray(session.assistants)) return session.assistants.map((assistant) => assistant.id);
  return [];
}

function allSame<T>(values: T[], comparer?: (left: T, right: T) => boolean) {
  if (values.length === 0) return true;
  const compare = comparer ?? ((left: T, right: T) => left === right);
  return values.every((value) => compare(value, values[0]));
}

function getLatestStartTime(sessions: Session[]) {
  if (!sessions || sessions.length === 0) return;
  let latestMinutes = -1;
  for (const session of sessions) {
    const parsed = parseISO(session.startTime);
    const minutes = parsed.getHours() * 60 + parsed.getMinutes();
    if (minutes > latestMinutes) {
      latestMinutes = minutes;
    }
  }
  if (latestMinutes < 0) return;
  const latest = new Date();
  latest.setHours(Math.floor(latestMinutes / 60), latestMinutes % 60, 0, 0);
  return latest;
}

function getBulkDefaults(sessions: Session[]): BulkDefaults {
  if (!sessions || sessions.length === 0) {
    return {
      values: {},
      mixed: {
        startDate: true,
        startTime: true,
        endTime: true,
        pastMidnight: true,
        company: true,
        contact: true,
        project: true,
        prePadHours: true,
        postPadHours: true,
        mixer: true,
        assistant: true,
        room: true,
        status: true,
        notes: true,
        unsupervised: true,
        noCharge: true
      }
    };
  }

  const startDates = sessions.map((session) => format(parseISO(session.startTime), 'yyyy-MM-dd'));
  const startTimes = sessions.map((session) => format(parseISO(session.startTime), 'HH:mm'));
  const endTimes = sessions.map((session) => format(parseISO(session.endTime), 'HH:mm'));
  const pastMidnights = sessions.map((session) => !isSameDay(parseISO(session.startTime), parseISO(session.endTime)));

  const companyIds = sessions.map((session) => session.companyId);
  const contactIds = sessions.map((session) => session.contactId);
  const projectIds = sessions.map((session) => session.projectId);
  const mixerIds = sessions.map((session) => session.mixerId);
  const roomIds = sessions.map((session) => session.roomId);

  const prePadHours = sessions.map((session) => session.prePadHours ?? '');
  const postPadHours = sessions.map((session) => session.postPadHours ?? '');
  const statuses = sessions.map((session) => session.status ?? '');
  const notes = sessions.map((session) => session.notes ?? '');
  const unsupervised = sessions.map((session) => Boolean(session.unsupervised));
  const noCharge = sessions.map((session) => Boolean(session.noCharge));

  const assistantGroups = sessions.map((session) => getAssistantIds(session));
  const assistantKeys = assistantGroups.map((ids) => [...ids].sort((a, b) => a - b).join(','));

  const startDateMixed = !allSame(startDates);
  const startTimeMixed = !allSame(startTimes);
  const endTimeMixed = !allSame(endTimes);
  const pastMidnightMixed = !allSame(pastMidnights);

  const companyMixed = !allSame(companyIds);
  const contactMixed = companyMixed ? true : !allSame(contactIds);
  const projectMixed = companyMixed ? true : !allSame(projectIds);

  const prePadMixed = !allSame(prePadHours);
  const postPadMixed = !allSame(postPadHours);
  const mixerMixed = !allSame(mixerIds);
  const assistantMixed = !allSame(assistantKeys);
  const roomMixed = !allSame(roomIds);
  const statusMixed = !allSame(statuses);
  const notesMixed = !allSame(notes);
  const unsupervisedMixed = !allSame(unsupervised);
  const noChargeMixed = !allSame(noCharge);

  return {
    values: {
      startDate: startDateMixed ? undefined : startDates[0],
      startTime: startTimeMixed ? undefined : startTimes[0],
      endTime: endTimeMixed ? undefined : endTimes[0],
      pastMidnight: pastMidnightMixed ? undefined : pastMidnights[0],
      companyId: companyMixed ? undefined : companyIds[0],
      contactId: contactMixed ? undefined : contactIds[0],
      projectId: projectMixed ? undefined : projectIds[0],
      prePadHours: prePadMixed ? undefined : prePadHours[0],
      postPadHours: postPadMixed ? undefined : postPadHours[0],
      mixerId: mixerMixed ? undefined : mixerIds[0],
      assistantIds: assistantMixed ? undefined : assistantGroups[0],
      roomId: roomMixed ? undefined : roomIds[0],
      status: statusMixed ? undefined : statuses[0],
      notes: notesMixed ? undefined : notes[0],
      unsupervised: unsupervisedMixed ? undefined : unsupervised[0],
      noCharge: noChargeMixed ? undefined : noCharge[0]
    },
    mixed: {
      startDate: startDateMixed,
      startTime: startTimeMixed,
      endTime: endTimeMixed,
      pastMidnight: pastMidnightMixed,
      company: companyMixed,
      contact: contactMixed,
      project: projectMixed,
      prePadHours: prePadMixed,
      postPadHours: postPadMixed,
      mixer: mixerMixed,
      assistant: assistantMixed,
      room: roomMixed,
      status: statusMixed,
      notes: notesMixed,
      unsupervised: unsupervisedMixed,
      noCharge: noChargeMixed
    }
  };
}

export function Menu() {
  const { user, selectedSessions, setSelectedSessions } = useStore();

  const matches = useMatches() as Match[];
  const isList = matches.at(-1).data?.isList;

  const location = useLocation();
  const isSearch = location.pathname.replace(/\/+$/, '') == '/schedule/search';

  const crumbs = matches
    .filter((match: any) => {
      return Boolean(match.handle?.crumb);
    })
    .map((match: any) => {
      return { name: match.handle?.crumb, link: match.pathname };
    });

  const [parameters] = useSearchParams();
  const selectedDate = parameters.get('d') || formatISO(new Date());
  const selectedDateStart = formatISO(parseISO(selectedDate).setHours(10, 0, 0, 0));
  const selectedDateEnd = formatISO(parseISO(selectedDate).setHours(18, 0, 0, 0));

  const revalidator = useRevalidator();
  const modal = React.useRef(null);
  const groupHelper = React.useRef(null);
  const modalDelete = React.useRef(null);
  const modalBulkDelete = React.useRef(null);
  const modalWarning = React.useRef(null);
  const changeTrackerReference = React.useRef<Record<string, boolean>>({});
  const [session, setSession] = React.useState<Session>();
  const [sessionSelect, setSessionSelect] = React.useState<Partial<Session>>();
  const [bulkEditSessions, setBulkEditSessions] = React.useState<Session[]>([]);
  const [bulkEditMode, setBulkEditMode] = React.useState(false);
  const [bulkEditLoading, setBulkEditLoading] = React.useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = React.useState(false);
  const [bulkDeleteIds, setBulkDeleteIds] = React.useState<number[]>([]);

  const isBulkEdit = bulkEditMode && bulkEditSessions.length > 0;
  const bulkDefaults = React.useMemo(() => getBulkDefaults(bulkEditSessions), [bulkEditSessions]);
  const bulkLatestStartTime = React.useMemo(() => getLatestStartTime(bulkEditSessions), [bulkEditSessions]);
  const cannotEditTitle = 'Cannot Edit';
  const cannotDeleteTitle = 'Cannot Delete';
  function createSession() {
    // eslint-disable-next-line unicorn/no-useless-undefined
    setSession(undefined);
    setSessionSelect({});
    setBulkEditSessions([]);
    setBulkEditMode(false);
    changeTrackerReference.current = {};
    modal.current.openModal();
  }
  function editSession(session: Session) {
    if (user?.role?.editSessions) {
      if (session.workOrder?.isFinished) {
        modalWarning.current.openModal('This session is finished and can no longer be edited.', cannotEditTitle);
      } else {
        setSession(session);
        setSessionSelect({});
        setBulkEditSessions([]);
        setBulkEditMode(false);
        changeTrackerReference.current = {};
        modal.current.openModal();
      }
    }
  }
  function deleteSession(session: Session) {
    if (user?.role?.editSessions) {
      if (session.workOrder) {
        modalWarning.current.openModal(
          'This session has a work order and cannot be deleted. First delete WO ' + session?.workOrder?.id + '.',
          cannotDeleteTitle
        );
      } else {
        setSession(session);
        modalDelete.current.openModal();
      }
    }
  }

  async function editSessions() {
    if (!user?.role?.editSessions || bulkEditLoading) return;
    const ids = [...new Set(selectedSessions)].filter((id): id is number => typeof id === 'number');
    if (ids.length === 0) return;
    setBulkEditLoading(true);
    try {
      const sessions = await getSessionsById(ids);
      if (!Array.isArray(sessions) || sessions.length === 0) {
        modalWarning.current.openModal('No sessions were found for the selected IDs.', cannotEditTitle);
        return;
      }
      setBulkEditSessions(sessions);
      setBulkEditMode(true);
      // eslint-disable-next-line unicorn/no-useless-undefined
      setSession(undefined);
      setSessionSelect({});
      changeTrackerReference.current = {};
      modal.current.openModal();
    } catch (error: any) {
      modalWarning.current.openModal(error?.message || 'Unable to load sessions.', cannotEditTitle);
    } finally {
      setBulkEditLoading(false);
    }
  }

  function sendOnlyChanged(formData: FormData) {
    const changeRecord = changeTrackerReference.current ?? {};
    const fieldErrors: Record<string, string> = {};

    const hasValue = (value: FormDataEntryValue | null) => typeof value === 'string' && value.length > 0;
    const parseNumber = (value: FormDataEntryValue | null) => {
      if (typeof value !== 'string' || value.length === 0) return 0;
      try {
        return Number.parseInt(JSON.parse(value), 10);
      } catch {
        return Number.parseInt(value, 10);
      }
    };

    const companyId = parseNumber(formData.get('companyId'));
    const contactId = parseNumber(formData.get('contactId'));
    const projectId = parseNumber(formData.get('projectId'));
    const mixerId = parseNumber(formData.get('mixerId'));
    const roomId = parseNumber(formData.get('roomId'));
    const statusValue = formData.get('status');
    const startDateValue = formData.get('startDate');
    const startTimeValue = formData.get('startTime');
    const endTimeValue = formData.get('endTime');

    const groupChanged = Boolean(changeRecord.company || changeRecord.contact || changeRecord.project);

    if (groupChanged) {
      if (!companyId) fieldErrors.companyId = 'Company is required';
      if (!contactId) fieldErrors.contactId = 'Contact is required';
      if (!projectId) fieldErrors.projectId = 'Project is required';
    } else {
      formData.delete('companyId');
      formData.delete('contactId');
      formData.delete('projectId');
    }

    if (!changeRecord.startDate) formData.delete('startDate');
    if (!changeRecord.startTime) formData.delete('startTime');
    if (!changeRecord.endTime) formData.delete('endTime');
    const pastMidnightOverride = changeRecord.pastMidnight
      ? formData.get('pastMidnight') === 'true' || formData.get('pastMidnight') === 'on'
      : undefined;
    if (changeRecord.pastMidnight) {
      const pastMidnightValue = formData.get('pastMidnight');
      formData.set('pastMidnight', pastMidnightValue ? 'true' : 'false');
    } else {
      formData.delete('pastMidnight');
    }

    if (changeRecord.startDate && !hasValue(formData.get('startDate'))) {
      fieldErrors.startDate = 'Select a start date';
    }
    if (changeRecord.startTime && !hasValue(formData.get('startTime'))) {
      fieldErrors.startTime = 'Select a start time';
    }
    if (changeRecord.endTime && !hasValue(formData.get('endTime'))) {
      fieldErrors.endTime = 'Select an end time';
    }

    if (!changeRecord.prePadHours) formData.delete('prePadHours');
    if (!changeRecord.postPadHours) formData.delete('postPadHours');
    if (!changeRecord.mixer) formData.delete('mixerId');
    if (!changeRecord.assistant) formData.delete('assistantIds');
    if (!changeRecord.room) formData.delete('roomId');
    if (!changeRecord.status) formData.delete('status');
    if (changeRecord.unsupervised) {
      const unsupervisedValue = formData.get('unsupervised');
      formData.set('unsupervised', unsupervisedValue ? 'true' : 'false');
    } else {
      formData.delete('unsupervised');
    }
    if (changeRecord.noCharge) {
      const noChargeValue = formData.get('noCharge');
      formData.set('noCharge', noChargeValue ? 'true' : 'false');
    } else {
      formData.delete('noCharge');
    }
    if (!changeRecord.notes) formData.delete('notes');

    if (changeRecord.mixer && !mixerId) fieldErrors.mixerId = 'Mixer is required';
    if (changeRecord.room && !roomId) fieldErrors.roomId = 'Room is required';
    if (changeRecord.status && !hasValue(statusValue)) fieldErrors.status = 'Session status is required';

    if (Object.keys(fieldErrors).length === 0) {
      const updatesStart = Boolean(changeRecord.startDate || changeRecord.startTime);
      const updatesEnd = Boolean(
        changeRecord.endTime || changeRecord.startDate || changeRecord.startTime || changeRecord.pastMidnight
      );
      if ((updatesStart || updatesEnd) && bulkEditSessions.length > 0) {
        const parsedStartDate =
          typeof startDateValue === 'string' && startDateValue.length > 0
            ? startOfDay(parseISO(startDateValue))
            : undefined;
        const parsedStartTime =
          typeof startTimeValue === 'string' && startTimeValue.length > 0 ? parseISO(startTimeValue) : undefined;
        const parsedEndTime =
          typeof endTimeValue === 'string' && endTimeValue.length > 0 ? parseISO(endTimeValue) : undefined;

        for (const session of bulkEditSessions) {
          const originalStart = parseISO(session.startTime);
          const originalEnd = parseISO(session.endTime);

          const updatedStart = new Date(originalStart);
          const updatedEnd = new Date(originalEnd);

          if (updatesStart || updatesEnd) {
            if (changeRecord.startDate && parsedStartDate) {
              updatedStart.setFullYear(
                parsedStartDate.getFullYear(),
                parsedStartDate.getMonth(),
                parsedStartDate.getDate()
              );
            }
            if (changeRecord.startTime && parsedStartTime && !Number.isNaN(parsedStartTime.getTime())) {
              updatedStart.setHours(parsedStartTime.getHours(), parsedStartTime.getMinutes(), 0, 0);
            }
          }

          if (updatesEnd) {
            const pastMidnightValue = changeRecord.pastMidnight
              ? Boolean(pastMidnightOverride)
              : !isSameDay(originalStart, originalEnd);
            const updatedEndDate = addDays(startOfDay(updatedStart), pastMidnightValue ? 1 : 0);
            updatedEnd.setFullYear(updatedEndDate.getFullYear(), updatedEndDate.getMonth(), updatedEndDate.getDate());
            if (changeRecord.endTime && parsedEndTime && !Number.isNaN(parsedEndTime.getTime())) {
              updatedEnd.setHours(parsedEndTime.getHours(), parsedEndTime.getMinutes(), 0, 0);
            }
          }

          if ((updatesStart || updatesEnd) && updatedStart.getTime() >= updatedEnd.getTime()) {
            fieldErrors.endTime = 'End date needs to be updated';
            break;
          }
        }
      }
    }

    if (Object.keys(fieldErrors).length > 0) {
      return { errors: fieldErrors };
    }
    formData.set('sessionIds', JSON.stringify(bulkEditSessions.map((session) => session.id)));
  }

  async function removeSessions() {
    if (!user?.role?.editSessions || bulkDeleteLoading) return;
    const ids = [...new Set(selectedSessions)].filter((id): id is number => typeof id === 'number');
    if (ids.length === 0) return;
    setBulkDeleteLoading(true);
    try {
      const sessions = await getSessionsById(ids);
      if (!Array.isArray(sessions) || sessions.length === 0) {
        modalWarning.current.openModal('No sessions were found for the selected IDs.', cannotDeleteTitle);
        return;
      }
      const hasWorkOrder = sessions.some((session) => {
        const workOrder = session.workOrder as unknown;
        return Array.isArray(workOrder) ? workOrder.length > 0 : Boolean(workOrder);
      });
      if (hasWorkOrder) {
        modalWarning.current.openModal(
          'One or more selected sessions have a work order and cannot be deleted. Remove the work order(s) first.',
          cannotDeleteTitle
        );
        return;
      }
      setBulkDeleteIds(ids);
      modalBulkDelete.current.openModal(ids);
    } catch (error: any) {
      modalWarning.current.openModal(error?.message || 'Unable to load sessions.', cannotDeleteTitle);
    } finally {
      setBulkDeleteLoading(false);
    }
  }

  function clearSelected() {
    setSelectedSessions([]);
  }

  function saveSession() {
    setSessionSelect({
      companyId: Number.parseInt(document.querySelector<HTMLInputElement>('input[name="companyId"]').value, 10),
      contactId: Number.parseInt(document.querySelector<HTMLInputElement>('input[name="contactId"]').value, 10),
      projectId: Number.parseInt(document.querySelector<HTMLInputElement>('input[name="projectId"]').value, 10),
      startTime: document.querySelector<HTMLInputElement>('input[name="startTime"]').value,
      endTime: document.querySelector<HTMLInputElement>('input[name="endTime"]').value,
      prePadHours: document.querySelector<HTMLSelectElement>('select[name="prePadHours"]').value,
      postPadHours: document.querySelector<HTMLSelectElement>('select[name="postPadHours"]').value,
      mixerId: Number.parseInt(document.querySelector<HTMLInputElement>('input[name="mixerId"]').value, 10),
      assistantIds: JSON.parse(document.querySelector<HTMLInputElement>('input[name="assistantIds"]')?.value || '[]'),
      roomId: Number.parseInt(document.querySelector<HTMLSelectElement>('select[name="roomId"]').value, 10),
      status: document.querySelector<HTMLSelectElement>('select[name="status"]').value,
      notes: document.querySelector<HTMLTextAreaElement>('textarea[name="notes"]').value,
      unsupervised: document.querySelector<HTMLInputElement>('input[name="unsupervised"]').checked,
      noCharge: document.querySelector<HTMLInputElement>('input[name="noCharge"]').checked
    });
  }

  const modalNotes = React.useRef(null);
  function showNotes(session: Session) {
    setSession(session);
    modalNotes.current.openModal();
  }

  const modalWorkOrderInfo = React.useRef(null);
  function showWorkOrderInfo(session: Session) {
    if (!session?.workOrder) {
      return;
    }
    setSession(session);
    modalWorkOrderInfo.current.openModal();
  }

  function getBackupLabel(backup?: string) {
    if (!backup) return '';
    return backupDrives.find((item) => item.key === backup)?.value || backup;
  }

  function getFrameRatesLabel(frameRateValues?: string[]) {
    if (!Array.isArray(frameRateValues)) return '';
    return frameRateValues
      .map((frameRate) => frameRates.find((item) => item.key === frameRate)?.value || frameRate)
      .join(', ');
  }

  const modalPta = React.useRef(null);
  const modalPtaDelete = React.useRef(null);
  const [pta, setPta] = React.useState<Pta>();
  const [excludedUsersLoader, setExcludedUsersLoader] = React.useState<number[]>([]);
  const [excludedUsers, setExcludedUsers] = React.useState<number[]>([]);
  function createPta() {
    // eslint-disable-next-line unicorn/no-useless-undefined
    setPta(undefined);
    setExcludedUsers(excludedUsersLoader);
    modalPta.current.openModal();
  }
  function editPta(pta: Pta) {
    setPta(pta);
    // remove edited user from excluded for typeahead form
    setExcludedUsers(excludedUsersLoader.filter((user) => user !== pta.userId));
    modalPta.current.openModal();
  }
  function deletePta(pta: Pta) {
    setPta(pta);
    modalPtaDelete.current.openModal();
  }

  const modalHoliday = React.useRef(null);
  const modalHolidayDelete = React.useRef(null);
  const [holiday, setHoliday] = React.useState<Holiday>();
  function createHoliday() {
    // eslint-disable-next-line unicorn/no-useless-undefined
    setHoliday(undefined);
    modalHoliday.current.openModal();
  }
  function editHoliday(holiday: Holiday) {
    setHoliday(holiday);
    modalHoliday.current.openModal();
  }
  function deleteHoliday(holiday: Holiday) {
    setHoliday(holiday);
    modalHolidayDelete.current.openModal();
  }

  const sessionTitle = isBulkEdit ? 'Edit Sessions' : (session ? 'Edit' : 'Add') + ' Session';
  const modalMethod = isBulkEdit ? 'patch' : undefined;
  const modalPutId = !isBulkEdit && session ? session?.id : undefined;
  const modalRedirectDate = isBulkEdit ? undefined : '/schedule';
  const modalReset = isBulkEdit ? () => revalidator.revalidate() : undefined;
  const allowKeepOpen = !isBulkEdit;
  const bulkDeleteLabel =
    bulkDeleteIds.length === 1
      ? 'this session'
      : bulkDeleteIds.length > 1
      ? `${bulkDeleteIds.length} sessions`
      : 'the selected sessions';

  return (
    <React.Fragment>
      <Container fluid={isList ? true : false}>
        {isList ? (
          <React.Fragment>
            <PageHeader crumbs={crumbs}>Schedule</PageHeader>
            <Nav>
              {selectedSessions.length > 0 && isSearch && (
                <React.Fragment>
                  <Nav.Link onClick={editSessions}>Edit Sessions</Nav.Link>
                  <Nav.Link onClick={removeSessions} disabled={bulkDeleteLoading}>
                    Remove Sessions
                  </Nav.Link>
                  <Nav.Link onClick={clearSelected}>Clear Selected</Nav.Link>
                </React.Fragment>
              )}
              {user?.role?.editSessions && <Nav.Link onClick={createSession}>Add Session</Nav.Link>}
              {user?.role?.editSessions && <Nav.Link onClick={createPta}>Add PTA</Nav.Link>}
              {user?.role?.users && <Nav.Link onClick={createHoliday}>Add Holiday</Nav.Link>}
              {!isSearch && <SearchBarDate />}
              <SearchButtonSessions moveRight={isSearch} draggable={true} />
            </Nav>
          </React.Fragment>
        ) : (
          <PageHeader crumbs={crumbs}>Edit Session</PageHeader>
        )}
        <Outlet
          context={{
            editSession,
            deleteSession,
            editHoliday,
            deleteHoliday,
            showNotes,
            showWorkOrderInfo,
            editPta,
            deletePta,
            setExcludedUsersLoader
          }}
        />
      </Container>

      <ModalForm
        title={sessionTitle}
        url='/schedule'
        putId={modalPutId}
        method={modalMethod}
        ref={modal}
        redirectDate={modalRedirectDate}
        reset={modalReset}
        beforeSubmit={isBulkEdit ? sendOnlyChanged : undefined}
        changeTrackerRef={changeTrackerReference}
        draggable
        allowKeepOpen={allowKeepOpen}
      >
        {isBulkEdit && <Form.Control type='hidden' name='sessionIds' value={JSON.stringify(selectedSessions)} />}
        <SelectorAPIGroup
          value={
            isBulkEdit
              ? {
                  companyId: bulkDefaults.values.companyId,
                  contactId: bulkDefaults.values.contactId,
                  projectId: bulkDefaults.values.projectId
                }
              : { companyId: session?.companyId, contactId: session?.contactId, projectId: session?.projectId }
          }
          valueSelect={{
            companyId: sessionSelect?.companyId,
            contactId: sessionSelect?.contactId,
            projectId: sessionSelect?.projectId
          }}
          addCompany={() => groupHelper.current.addCompany()}
          addContact={() => groupHelper.current.addContact()}
          linkContact={() => groupHelper.current.linkContact()}
          addProject={() => groupHelper.current.addProject()}
        />
        <SelectorDateTime
          value={
            isBulkEdit
              ? {
                  startDate: bulkDefaults.values.startDate,
                  startTime: bulkDefaults.values.startTime,
                  endTime: bulkDefaults.values.endTime,
                  pastMidnight: bulkDefaults.values.pastMidnight
                }
              : { startTime: session?.startTime || selectedDateStart, endTime: session?.endTime || selectedDateEnd }
          }
          valueSelect={{
            startTime: sessionSelect?.startTime,
            endTime: sessionSelect?.endTime
          }}
          pastMidnightMixed={isBulkEdit ? bulkDefaults.mixed.pastMidnight : false}
          bulkLatestStartTime={isBulkEdit ? bulkLatestStartTime : undefined}
        />
        <SelectorPad
          name='prePadHours'
          value={isBulkEdit ? bulkDefaults.values.prePadHours : session?.prePadHours}
          valueSelect={sessionSelect?.prePadHours}
        />
        <SelectorPad
          name='postPadHours'
          value={isBulkEdit ? bulkDefaults.values.postPadHours : session?.postPadHours}
          valueSelect={sessionSelect?.postPadHours}
        />
        <SelectorAPI
          name='mixer'
          size='col-12'
          fetchURL='/users/mixers'
          value={isBulkEdit ? bulkDefaults.values.mixerId : session?.mixerId}
          valueSelect={sessionSelect?.mixerId}
        />
        <SelectorAPIMultiple
          name='assistant'
          size='col-12'
          fetchURL='/users/assistants'
          value={isBulkEdit ? bulkDefaults.values.assistantIds : session?.assistantIds}
          valueSelect={sessionSelect?.assistantIds}
        />
        <SelectorAPISimple
          name='room'
          size='col-6'
          fetchURL='/rooms'
          value={isBulkEdit ? bulkDefaults.values.roomId : session?.roomId}
          valueSelect={sessionSelect?.roomId}
        />
        <SelectorList
          name='status'
          list={allStatuses}
          size='col-6'
          value={isBulkEdit ? bulkDefaults.values.status : session?.status}
          valueSelect={sessionSelect?.status}
        />
        <InputTextArea
          name='notes'
          value={isBulkEdit ? bulkDefaults.values.notes : session?.notes}
          valueSelect={sessionSelect?.notes}
        />
        <Switch
          name='unsupervised'
          value={isBulkEdit ? bulkDefaults.values.unsupervised : session?.unsupervised}
          valueSelect={sessionSelect?.unsupervised}
          mixed={isBulkEdit ? bulkDefaults.mixed.unsupervised : false}
        />
        <Switch
          name='noCharge'
          value={isBulkEdit ? bulkDefaults.values.noCharge : session?.noCharge}
          valueSelect={sessionSelect?.noCharge}
          mixed={isBulkEdit ? bulkDefaults.mixed.noCharge : false}
        />
      </ModalForm>

      <SelectorAPIHelper
        elementSelect={sessionSelect}
        setElementSelect={setSessionSelect}
        saveForm={saveSession}
        modal={modal}
        ref={groupHelper}
      />

      <ModalWarning title='Notes' size='lg' ref={modalNotes}>
        <InfoTable
          elements={[
            { title: 'Company', content: session?.company?.notes },
            { title: 'Contact', content: session?.contact?.notes },
            { title: 'Project', content: session?.project?.notes },
            ...(user?.role?.privateNotes && session?.project?.privateNotes?.length > 0
              ? [{ title: 'Producer', content: session?.project?.privateNotes }]
              : []),
            { title: 'Session', content: session?.notes },
            { title: 'Assist Notes', content: session?.workOrder?.notes }
          ]}
          hideEmpty
        ></InfoTable>
      </ModalWarning>

      <ModalWarning
        title='Titles and Technical Data'
        size='xl'
        dialogClassName='workorder-info-modal'
        ref={modalWorkOrderInfo}
      >
        <Form>
          <Row>
            <Form.Group controlId='view-backup' className='mt-3 col-12 col-md-4'>
              <Form.Label>Unified Backup</Form.Label>
              <Form.Control readOnly value={getBackupLabel(session?.workOrder?.backup)} />
            </Form.Group>
            <Form.Group controlId='view-frameRates' className='mt-3 col-12 col-md-8'>
              <Form.Label>Frame Rate(s)</Form.Label>
              <Form.Control readOnly value={getFrameRatesLabel(session?.workOrder?.frameRates)} />
            </Form.Group>
            <Form.Group controlId='view-voTalent' className='mt-3 col-12'>
              <Form.Label>VO Talent</Form.Label>
              <Form.Control as='textarea' rows={4} readOnly value={session?.workOrder?.voTalent || ''} />
            </Form.Group>
            <div className='w-100'>
              <hr className='my-3' />
            </div>
            <Form.Group controlId='view-titles' className='mt-3 col-12 col-lg-6'>
              <Form.Label>Spot Titles</Form.Label>
              <Form.Control as='textarea' rows={18} readOnly value={session?.workOrder?.titles || ''} />
            </Form.Group>
            <Form.Group controlId='view-filename' className='mt-3 col-12 col-lg-6'>
              <Form.Label>Pro Tools Session Name(s)</Form.Label>
              <Form.Control as='textarea' rows={18} readOnly value={session?.workOrder?.filename || ''} />
            </Form.Group>
          </Row>
        </Form>
      </ModalWarning>

      <ModalWarning ref={modalWarning} />

      <ModalDelete title='Delete Session' url='/schedule' ref={modalDelete} deleteId={session?.id}>
        Are you sure you want to delete this session?
      </ModalDelete>

      <ModalDelete
        title='Remove Sessions'
        url='/schedule'
        ref={modalBulkDelete}
        button='Remove'
        onSuccess={() => {
          setSelectedSessions([]);
          setBulkDeleteIds([]);
        }}
      >
        Are you sure you want to remove {bulkDeleteLabel}?
      </ModalDelete>

      <ModalForm
        title={(session ? 'Edit' : 'Add') + ' PTA'}
        url='/users/pta'
        putId={pta ? pta?.id : undefined}
        ref={modalPta}
        draggable
      >
        <SelectorPta
          value={pta?.date || formatISO(startOfDay(parseISO(selectedDate)))}
          userId={pta?.userId}
          excludedUsers={excludedUsers}
          allowPast={user?.role?.users}
        />
      </ModalForm>
      <ModalDelete title='Delete PTA' url='/users/pta' ref={modalPtaDelete} deleteId={pta?.id}>
        Are you sure you want to delete this PTA?
      </ModalDelete>

      <ModalForm
        title={(holiday ? 'Edit' : 'Add') + ' Holiday'}
        url='/schedule/holiday'
        putId={holiday ? holiday?.id : undefined}
        ref={modalHoliday}
        draggable
      >
        <SelectorDate value={holiday?.date || formatISO(startOfDay(parseISO(selectedDate)))} trackChanges={false} />
      </ModalForm>
      <ModalDelete title='Delete Holiday' url='/schedule/holiday' ref={modalHolidayDelete} deleteId={holiday?.id}>
        Are you sure you want to delete this holiday?
      </ModalDelete>
    </React.Fragment>
  );
}
